import React, { useEffect, useState } from 'react';
import { Table } from 'react-bootstrap';
import StatusBadge from './StatusBadge';
import RequirementDateRangeFilter from './RequirementDateRangeFilter';
import { getRecruiterWorkLog } from '../../services/workflowService';
import { useLayout } from '../../layout/AppLayout';
import { formatDisplayDate } from '../../utils/dateFormat';
import { getDefaultDashboardDateRange } from '../../utils/requirementDateFilter';

const DEFAULT_WORK_DATE_RANGE = getDefaultDashboardDateRange();

function RecruiterTodayWork({ userId, showTitle = true }) {
  const { tableRefresh } = useLayout();
  const [fromDate, setFromDate] = useState(DEFAULT_WORK_DATE_RANGE.from);
  const [toDate, setToDate] = useState(DEFAULT_WORK_DATE_RANGE.to);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');

  useEffect(() => {
    if (!userId) return undefined;

    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setLoadError('');
      try {
        const response = await getRecruiterWorkLog(userId, fromDate, toDate);
        if (!cancelled) {
          setData(response);
        }
      } catch (error) {
        if (!cancelled) {
          console.error('Recruiter work log load failed:', error);
          setLoadError(error.message || 'Failed to load work summary.');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [userId, fromDate, toDate, tableRefresh.candidates, tableRefresh.requirements]);

  const summary = data?.summary || {};
  const profiles = data?.profiles || [];
  const requirements = data?.requirements || [];

  return (
    <div className="hr-table-card mb-4">
      <div className="hr-section-toolbar">
        {showTitle ? (
          <h3 className="hr-section-title" style={{ margin: 0 }}>
            <img src="/Images/icon.png" alt="" />
            Today&apos;s Work
          </h3>
        ) : null}
        <RequirementDateRangeFilter
          fromDate={fromDate}
          toDate={toDate}
          onFromChange={setFromDate}
          onToChange={setToDate}
        />
      </div>

      {loadError ? <div className="alert alert-danger mb-3">{loadError}</div> : null}

      <div className="hr-stat-grid mb-3">
        <div className="hr-mini-stat">
          <span className="hr-mini-stat-label">Profiles Submitted</span>
          <strong>{summary.profilesSubmitted || 0}</strong>
        </div>
        <div className="hr-mini-stat">
          <span className="hr-mini-stat-label">Requirements Worked</span>
          <strong>{summary.requirementsWorked || 0}</strong>
        </div>
      </div>

      {loading ? (
        <p className="hr-empty-state">Loading work summary...</p>
      ) : (
        <>
          <h4 className="hr-subsection-title">Profiles Submitted</h4>
          <Table className="hr-table mb-4" responsive="sm">
            <thead>
              <tr>
                <th>Profile</th>
                <th>Req ID</th>
                <th>Client</th>
                <th>Role</th>
                <th>Status</th>
                <th>Submitted On</th>
              </tr>
            </thead>
            <tbody>
              {profiles.length ? profiles.map((item) => (
                <tr key={`${item.candidateId}-${item.uploadedOn}`}>
                  <td>{item.candidateName}</td>
                  <td>{item.regId}</td>
                  <td>{item.client}</td>
                  <td>{item.role}</td>
                  <td><StatusBadge status={item.status} useStoredLabel /></td>
                  <td>{item.uploadedOn ? formatDisplayDate(item.uploadedOn) : '—'}</td>
                </tr>
              )) : (
                <tr>
                  <td colSpan="6">No profiles submitted in this date range.</td>
                </tr>
              )}
            </tbody>
          </Table>

          <h4 className="hr-subsection-title">Requirements Worked</h4>
          <Table className="hr-table" responsive="sm">
            <thead>
              <tr>
                <th>Req ID</th>
                <th>Client</th>
                <th>Role</th>
                <th>Profiles Submitted</th>
                <th>Status Updates</th>
                <th>Last Worked On</th>
              </tr>
            </thead>
            <tbody>
              {requirements.length ? requirements.map((item) => (
                <tr key={item.requirementId}>
                  <td>{item.regId}</td>
                  <td>{item.client}</td>
                  <td>{item.role}</td>
                  <td>{item.profilesSubmitted}</td>
                  <td>{item.statusUpdates}</td>
                  <td>{item.lastWorkedOn ? formatDisplayDate(item.lastWorkedOn) : '—'}</td>
                </tr>
              )) : (
                <tr>
                  <td colSpan="6">No requirements worked in this date range.</td>
                </tr>
              )}
            </tbody>
          </Table>
        </>
      )}
    </div>
  );
}

export default RecruiterTodayWork;
